import Cookies from 'js-cookie';
import { computed, makeObservable, observable, runInAction } from 'mobx';
import { NavigateFunction } from 'react-router-dom';
import { PaginationControl } from 'shared/components/pagination';
import { AsyncState } from 'shared/libs/state';
import { DeleteSavedJobResponse, SavedJob, SavedJobsIndexResponse } from './types';
import { SavedJobsApiService } from './saved-jobs.api.service';

export class SavedJobsStore {
   readonly loadSavedJobsState = new AsyncState<SavedJobsIndexResponse>();
   readonly deleteSavedJobState = new AsyncState<DeleteSavedJobResponse>();
   readonly pagination = new PaginationControl();

   private readonly PER_PAGE = 10;

   rawSavedJobs: SavedJob[] = [];
   deletingShortListId: number | null = null;

   constructor(private readonly navigate: NavigateFunction) {
      makeObservable(this, {
         rawSavedJobs: observable,
         deletingShortListId: observable,
         savedJobs: computed,
         totalPages: computed,
         canNext: computed,
         canPrev: computed,
         recordsCount: computed,
         coef: computed,
      });
   }

   get savedJobs(): SavedJob[] {
      const firstIdx = this.pagination.page * this.PER_PAGE;
      return this.rawSavedJobs.slice(firstIdx, firstIdx + this.PER_PAGE);
   }

   get totalPages(): number {
      return Math.ceil(this.rawSavedJobs.length / this.PER_PAGE);
   }

   get canPrev(): boolean {
      return this.pagination.page > 0;
   }

   get canNext(): boolean {
      const next = this.pagination.page + 1;
      return next < this.totalPages;
   }

   get recordsCount(): string {
      if (!this.rawSavedJobs.length) {
         return '0 - 0';
      }

      const firstRecord = this.pagination.page * this.PER_PAGE + 1;
      let lastRecord = firstRecord + this.PER_PAGE - 1;
      if (lastRecord > this.rawSavedJobs.length) {
         lastRecord = this.rawSavedJobs.length;
      }

      return `${firstRecord} - ${lastRecord}`;
   }

   get coef(): number {
      return this.pagination.page * this.PER_PAGE + 1;
   }

   onPrev(): void {
      if (this.canPrev) {
         this.pagination.setPage(this.pagination.page - 1);
      }
   }

   onNext(): void {
      if (this.canNext) {
         this.pagination.setPage(this.pagination.page + 1);
      }
   }

   openJobDescription(job: SavedJob): void {
      sessionStorage.setItem('id', String(job.id));
      this.navigate(`/jobdescription/${job.slug}/${job.cat_slug}`);
   }

   async init(): Promise<void> {
      if (!this.ensureCandidateAccess()) {
         return;
      }

      await this.loadSavedJobs();
   }

   async loadSavedJobs(): Promise<void> {
      await this.loadSavedJobsState.run(() => SavedJobsApiService.loadSavedJobs(null), {
         onSuccess: (resp) => {
            if (resp.status === 200) {
               runInAction(() => {
                  this.rawSavedJobs = resp.response.ShortLists ?? [];
                  this.normalizePage();
               });
            }
         },
         onError: (err) => {
            this.handleExpiredOrUnauthorized(err.message);
         },
      });
   }

   async deleteSavedJob({
      shortListId,
      onSuccess,
      onError,
   }: {
      shortListId: number;
      onSuccess?: (response: DeleteSavedJobResponse) => void;
      onError?: (message: string) => void;
   }): Promise<void> {
      runInAction(() => {
         this.deletingShortListId = shortListId;
      });

      await this.deleteSavedJobState.run(() => SavedJobsApiService.deleteSavedJob(shortListId), {
         onSuccess: async (resp) => {
            if (resp.status === 200) {
               onSuccess?.(resp);
               await this.loadSavedJobs();
            } else {
               onError?.(resp.message || 'Request failed');
            }
         },
         onError: (err) => {
            this.handleExpiredOrUnauthorized(err.message);
            onError?.(err.message);
         },
         onFinally: () => {
            runInAction(() => {
               this.deletingShortListId = null;
            });
         },
      });
   }

   private normalizePage(): void {
      const maxPage = Math.max(0, this.totalPages - 1);
      if (this.pagination.page > maxPage) {
         this.pagination.setPage(maxPage);
      }
   }

   private ensureCandidateAccess(): boolean {
      const userType = Cookies.get('user_type');
      const token = Cookies.get('tokenClient');

      if (!token || userType !== 'candidate') {
         this.navigate('/auth/candidate-login');
         return false;
      }

      return true;
   }

   private handleExpiredOrUnauthorized(message: string): void {
      if (message.toLowerCase().includes('token has expired') || message.toLowerCase().includes('unauthorized')) {
         this.navigate('/auth/candidate-login');
      }
   }
}
